import {
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import ReportNotesService from 'modules/projects/api/ReportNotesService';
import { asErrorData, DataState } from 'slices';
import { ErrorData, GenericDataState } from 'type';
import { ReportNote, ReportNoteDisplay, UpdateDynamicReportNoteModel, UpdateReportNoteModel } from 'types/reportNotes';

type ReportNotesDataState = GenericDataState<ReportNote> & {
  isDynamic?: boolean;
  queryParams?: {
    projectId: string;
  };
};

export const getReportNotes = createAsyncThunk(
  'reportNotes/getReportNotes',
  async (
    params: {
      projectId: string;
    },
    thunkApi
  ) => {
    try {
      const result = await ReportNotesService.getReportNotes(params.projectId);
      return result.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const updateReportNotes = createAsyncThunk(
  'reportNotes/updateReportNotes',
  async (
    params: {
      useDynamic?: boolean;
      reportNotesData: UpdateDynamicReportNoteModel | UpdateReportNoteModel,
    },
    thunkApi
  ) => {
    try {
      const result = await ReportNotesService.updateReportNotes(params.reportNotesData, params.useDynamic);
      return result.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

const initialState: ReportNotesDataState = {
  data: {} as ReportNote,
  queryParams: undefined,
  dataState: DataState.INITIAL,
};

const reportNotesSlice = createSlice({
  name: 'reportNotes',
  initialState,
  reducers: {
    setQueryParams: (state, action) => {
      state.queryParams = action.payload;
    },
    expireReportNotes: (state, action: { payload: { expireData: boolean; }; }) => {
      if (action?.payload?.expireData) {
        state.data = {} as ReportNote;
        state.queryParams = undefined;
      }
      state.dataState = DataState.INITIAL;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getReportNotes.pending, (state) => {
      state.dataState = DataState.LOADING;
    });
    builder.addCase(getReportNotes.fulfilled, (state, action) => {
      state.dataState = DataState.FULFILLED;
      state.data = action.payload;
    });
    builder.addCase(getReportNotes.rejected, (state, action) => {
      if (action.payload) {
        state.error = action.payload as ErrorData;
      } else {
        state.error = asErrorData(action.error);
      }
      state.dataState = DataState.ERROR;
    });
    // builder.addCase(updateReportNotes.pending, (state) => {
    //   state.dataState = DataState.LOADING;
    // });
    builder.addCase(updateReportNotes.fulfilled, (state) => {
      state.dataState = DataState.FULFILLED;
    });
    builder.addCase(updateReportNotes.rejected, (state, action) => {
      if (action.payload) {
        state.error = action.payload as ErrorData;
      } else {
        state.error = asErrorData(action.error);
      }
      state.dataState = DataState.ERROR;
    });
  },
});

export default reportNotesSlice.reducer;

export const selectCurrentReportNotesId = (state: { reportNotes: ReportNotesDataState }): string | undefined => state.reportNotes.data?.id;
export const selectCurrentReportNotes = (state: { reportNotes: ReportNotesDataState }): ReportNoteDisplay =>
  (state.reportNotes.data ?? {}) as ReportNoteDisplay;
export const selectReportNoteDataState = (state: { reportNotes: ReportNotesDataState }): DataState => state.reportNotes.dataState;

export const { expireReportNotes, setQueryParams } = reportNotesSlice.actions;
